import { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Typography,
} from '@mui/material';
import { useSetCashDeskPin } from '../../api/queries';

/**
 * Set (or replace) a user's cash-desk PIN (plan Draft 1 W1/W3). The PIN is a
 * DESK login factor only — never the portal password. 4–6 digits, entered
 * twice; sent once, never echoed back, never shown again.
 */
export function SetCashDeskPinDialog({ userId, userName, onClose }: {
  userId: string | null;
  userName: string;
  onClose: () => void;
}) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const setPinMutation = useSetCashDeskPin(userId);

  useEffect(() => {
    if (userId !== null) { setPin(''); setConfirm(''); setDone(false); setPinMutation.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only
  }, [userId]);

  const shapeOk = /^\d{4,6}$/.test(pin);
  const matches = pin === confirm;

  return (
    <Dialog open={userId !== null} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set cash-desk PIN</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity="success" data-testid="pin-success">
            Cash-desk PIN set for <strong>{userName}</strong>. It reaches their site's
            cash desk on the next config sync.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              A desk login factor for <strong>{userName}</strong> — used only at the
              cash desk, never for the portal.
            </Typography>
            <TextField
              label="PIN (4–6 digits)"
              type="password"
              required
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              error={pin !== '' && !shapeOk}
              helperText={pin !== '' && !shapeOk ? 'Digits only, 4 to 6 of them.' : ' '}
              inputProps={{ inputMode: 'numeric', maxLength: 6, 'data-testid': 'pin-input' }}
            />
            <TextField
              label="Confirm PIN"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              error={confirm !== '' && !matches}
              helperText={confirm !== '' && !matches ? 'PINs do not match.' : ' '}
              inputProps={{ inputMode: 'numeric', maxLength: 6, 'data-testid': 'pin-confirm' }}
            />
            {setPinMutation.isError && (
              <Alert severity="error">
                {(setPinMutation.error as { message?: string })?.message ?? 'Failed to set the PIN.'}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button
            variant="contained"
            disabled={!shapeOk || !matches || setPinMutation.isPending}
            data-testid="pin-submit"
            onClick={() => setPinMutation.mutate(pin, { onSuccess: () => setDone(true) })}
          >
            Set PIN
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
